import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";
import Nav from "@/components/Nav";
import IntakeForm from "@/components/IntakeForm";
import LockedAudits from "@/components/LockedAudits";
import PricingMatrix from "@/components/PricingMatrix";
import Footer from "@/components/Footer";
import { initiateSweep, getSweepJob, getSweep, getAccountMe } from "@/lib/api";
import { useAcceptanceGate } from "@/components/legal/useAcceptanceGate";
import { Search, Layers, LineChart, Radio, Ban, Scale } from "lucide-react";

const ParticlePlumeScene = lazy(() => import("@/components/ParticlePlumeScene"));
const Simulation = lazy(() => import("@/components/Simulation"));

/**
 * Small diagrams under each method stat. The section previously carried three
 * large numerals and a paragraph each, which read as a text block rather than a
 * composed panel. Each graphic restates its own stat visually, so the caption
 * underneath can be short.
 *
 * Decorative only - no figure here is derived from a sweep, so none of it can
 * drift out of sync with measured data. aria-hidden for that reason.
 */
function StatGraphic({ kind }) {
  const common = "mt-5 w-full max-w-[190px] h-[52px]";

  if (kind === "feeds") {
    // Two series compared - ink is you, accent is the competitor.
    const you = [30, 20, 38, 26, 34];
    const rival = [22, 28, 24, 36, 18];
    return (
      <svg viewBox="0 0 190 52" className={common} fill="none" aria-hidden="true">
        {you.map((a, i) => (
          <g key={i}>
            <rect x={6 + i * 36} y={44 - a} width="13" height={a} fill="#171717" />
            <rect x={21 + i * 36} y={44 - rival[i]} width="13" height={rival[i]} fill="#e88a51" />
          </g>
        ))}
        <line x1="0" y1="44.5" x2="190" y2="44.5" stroke="#a8a196" strokeWidth="1" />
      </svg>
    );
  }

  if (kind === "modules") {
    // Four solid rows unlocked, four dashed rows still locked behind the audit.
    const open = [186, 158, 172, 150];
    const shut = [140, 116, 132, 104];
    return (
      <svg viewBox="0 0 190 52" className={common} fill="none" aria-hidden="true">
        {open.map((w, i) => (
          <rect key={i} x="2" y={i * 6.4} width={w} height="4.4" rx="2.2" fill="#e88a51" />
        ))}
        {shut.map((w, i) => (
          <rect key={i} x="2" y={27 + i * 6.4} width={w} height="4.4" rx="2.2"
                stroke="#a8a196" strokeWidth="1" strokeDasharray="3 3" />
        ))}
      </svg>
    );
  }

  // "gaps" - a measured grid where the unanswerable cells are struck, not filled.
  const struck = new Set([4, 9]);
  return (
    <svg viewBox="0 0 190 52" className={common} fill="none" aria-hidden="true">
      {Array.from({ length: 12 }).map((_, i) => {
        const x = 2 + (i % 6) * 31;
        const y = 6 + Math.floor(i / 6) * 22;
        return struck.has(i) ? (
          <g key={i}>
            <rect x={x} y={y} width="26" height="14" rx="2" stroke="#a8a196" strokeWidth="1" />
            <line x1={x + 4} y1={y + 11} x2={x + 22} y2={y + 3} stroke="#9a3412" strokeWidth="1.4" />
          </g>
        ) : (
          <rect key={i} x={x} y={y} width="26" height="14" rx="2" fill="#171717" opacity="0.82" />
        );
      })}
    </svg>
  );
}

const FEATURES = [
  {
    icon: Search,
    tag: "Module 01",
    title: "Margin Risk Signals",
    lede: "Directional margin risks measured from live public data — discount exposure, stockout drag, catalog bloat — each flagged by severity.",
    bullets: [
      "Every observation measured, never modelled",
      "Severity-ranked with the exact data point behind it",
      "Where public data ends, the paid audit begins — stated plainly",
    ],
  },
  {
    icon: Layers,
    tag: "Module 02",
    title: "Catalog Variation Density Mapping",
    lede: "Your SKU footprint compared against a lean competitor cohort — trapped inventory rendered in one glance.",
    bullets: [
      "Products, variants and variant density from both live feeds",
      "Sold-out variant share — stock discipline in one number",
      "Written verdict grounded in the measured numbers",
    ],
  },
  {
    icon: LineChart,
    tag: "Module 03",
    title: "Retail Price Positioning",
    lede: "Median and quartile shelf prices, on-sale share and discount depth — you versus your competitor, measured live.",
    bullets: [
      "Median + P25–P75 price bands for both stores",
      "On-sale share and average discount depth",
      "Per-category medians where feeds expose product types",
    ],
  },
  {
    icon: Scale,
    tag: "Module 04",
    title: "Unit & Pack Economics",
    lede: "Price per millilitre, gram or item — plus the multipacks and size ladders that quietly punish the customer for buying more.",
    bullets: [
      "Multipacks that cost more per unit than your own singles",
      "Larger sizes priced above smaller ones per unit",
      "Every figure states the share of titles its size could be read from",
    ],
  },
];

// The briefing itself lives at /report/:id, not on the homepage - the homepage
// stays a marketing page. But the pricing grid still needs to know which sweep
// a visitor just ran, so a paid audit can be attached to it, and that outlives
// the navigation away. Only an id is kept: it is not secret on its own, the
// report behind it is login-gated and ownership-checked server-side.
const LAST_REPORT_KEY = "md_last_report_id";

// A sweep is documented at 30-110s. Past SLOW we tell the visitor it is running
// long rather than pretending nothing has happened; past ABANDON we stop
// holding them at the screen, because the job finishes server-side either way
// and the briefing is theirs in My Account.
const SWEEP_SLOW_AFTER_MS = 120000;
const SWEEP_ABANDON_AFTER_MS = 300000;

// Why a store could not be read, in the prospect's language. The backend
// decides which of these applies per store (server.py `_why_no_catalog`); the
// distinction matters because the fixes are completely different - one is a
// typo the visitor can correct, one is the store's own firewall and is not
// ours to route around.
const NO_FEED_REASON = {
  unresolved:
    "that domain does not resolve. Check the spelling: there is no site at that address.",
  blocked:
    "the store answered, then refused our crawler. That is the store's own setting, not something you did.",
  unreachable: "the store did not respond.",
  no_catalog: "the store is reachable but publishes no product feed we can read.",
  // Operator-approved 2 Oct 2026: a store that answers 429 is left alone.
  throttled: "the store asked us to slow down, so we stopped. Try again later.",
  ok: "read successfully.",
};

const anyBlocked = (detail) =>
  Boolean(detail) && ["your", "competitor"].some((s) => detail[s]?.reason === "blocked");

// A sweep now stops when EITHER store is unreadable, so the panel has to say
// which one - and the store that read fine reports "ok". Blaming both when only
// the competitor failed sends the visitor to fix the wrong thing.
const failedSides = (detail) =>
  !detail ? [] : ["your", "competitor"].filter(
    (s) => detail[s] && detail[s].reason !== "ok");

const noFeedHeading = (detail) => {
  const failed = failedSides(detail);
  if (failed.length !== 1) {
    return anyBlocked(detail)
      ? "We could not read either store."
      : "Neither store publishes a public product feed.";
  }
  return failed[0] === "competitor"
    ? "We could not read that competitor."
    : "We could not read your store.";
};

export default function Home() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("idle");
  const [lastReportId, setLastReportId] = useState(() => {
    try {
      return sessionStorage.getItem(LAST_REPORT_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ stage: null, index: 0, count: 5, note: null });
  const [noFeedDetail, setNoFeedDetail] = useState(null);
  // The allowance is enforced server-side at /sweep/initiate; this is only so
  // the form can say what is left before the visitor spends one. Refreshed
  // after every sweep, because the count has just changed.
  const [sweepsRemaining, setSweepsRemaining] = useState(null);
  const reportSectionRef = useRef(null);
  // The briefing is the deliverable, so it is the thing that must not appear
  // without a recorded acceptance behind it. The backend refuses with 428 and
  // this raises the consent step in response.
  const { guard, gateNode } = useAcceptanceGate();

  // Read once on sign-in, and again after a sweep spends one.
  const refreshAllowance = useRef(null);
  refreshAllowance.current = async () => {
    if (!isSignedIn) {
      setSweepsRemaining(null);
      return;
    }
    try {
      const me = await getAccountMe();
      setSweepsRemaining(me?.free_sweeps_remaining ?? null);
    } catch {
      // Display only - the server is what enforces the limit.
      setSweepsRemaining(null);
    }
  };

  useEffect(() => {
    refreshAllowance.current?.();
  }, [isSignedIn]);

  const revealReport = useRef(null);

  // The sweep cannot start without a session, so by the time a report exists
  // the visitor is already signed in. That removed a whole state machine: the
  // "briefing ready, now sign in" phase, the sessionStorage handoff across a
  // redirect sign-in, and the effect that fetched the report once a session
  // appeared. All three existed only to carry an anonymous sweep across a
  // login, and all three were where this page's worst bugs lived.
  //
  // The fetch stays here rather than being left to /report/:id because it is
  // the call the consent gate refuses with 428, so this is where the modal must
  // be raised. Once it succeeds we hand the visitor to the briefing's own page.
  revealReport.current = async (id, message) => {
    await getSweep(id);
    try {
      sessionStorage.setItem(LAST_REPORT_KEY, id);
    } catch {
      // Private browsing with storage denied. Attribution is a nice-to-have.
    }
    setLastReportId(id);
    setPhase("idle");
    toast.success(message);
    navigate(`/report/${id}`);
  };

  const handleSubmit = async ({ your_domain, competitor_domain, accept_terms }) => {
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      setLoading(true);
      setPhase("simulating");
      setProgress({ stage: "Starting sweep", index: 0, count: 5, note: null });

      const { job_id } = await initiateSweep(your_domain, competitor_domain, null, accept_terms);

      // The sweep runs in the background; poll until it resolves. The old code
      // waited on one synchronous request that would be killed by most hosts.
      const startedAt = Date.now();
      let slowNotified = false;
      for (;;) {
        await sleep(1500);
        // Slow is not failed. The job runs server-side and writes its report
        // whatever the browser does, so abandoning the poll at a fixed ceiling
        // used to strand a briefing that existed and was already paid for.
        // Past the expected window we say so and keep waiting; the hard stop
        // exists only so a truly dead job does not spin forever, and by then
        // the report is in My Account if it ever lands.
        const waited = Date.now() - startedAt;
        if (waited > SWEEP_SLOW_AFTER_MS && !slowNotified) {
          slowNotified = true;
          setProgress((p) => ({
            ...p,
            note: "This one is taking longer than usual. It is still running — "
                  + "you can wait here, or find it in My Account when it lands.",
          }));
        }
        if (waited > SWEEP_ABANDON_AFTER_MS) {
          toast("Still running. It will appear in My Account when it finishes.",
                { duration: 9000 });
          setPhase("idle");
          return;
        }

        const job = await getSweepJob(job_id);
        setProgress({
          stage: job.stage,
          index: job.stage_index ?? 0,
          count: job.stage_count ?? 5,
          note: job.note,
        });

        if (job.status === "done" && job.report_id) {
          await guard("report_view",
            () => revealReport.current(job.report_id, "Sweep complete. Opening your briefing."));
          break;
        }
        if (job.status === "no_feed") {
          setNoFeedDetail(job.no_feed_detail || null);
          setPhase("no_feed");
          break;
        }
        if (job.status === "error") {
          // Surface what actually broke. A generic "sweep failed" gives the
          // operator nothing to act on and hides real backend errors.
          throw new Error(job.error || "Sweep failed");
        }
      }
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        toast.error(detail || "Sweep limit reached. Try again in an hour.");
      } else if (status === 402) {
        // Allowance exhausted. Not an error the visitor can retry out of, so
        // it says what is left to do rather than "try again".
        toast.error(detail || "You have used all your free sweeps on this account.",
                    { duration: 10000 });
      } else if (status === 401) {
        toast.error("Please sign in to run a sweep.");
      } else if (status === 400) {
        toast.error(detail || "Check both domains and try again.");
      } else if (e?.message && e.message !== "Sweep failed") {
        toast.error(e.message, { duration: 8000 });
      } else {
        toast.error("Sweep failed. Please try again.");
      }
      setPhase("idle");
    } finally {
      setLoading(false);
      // The sweep spent an allowance whatever the outcome - it is charged at
      // initiate, not on a successful briefing.
      refreshAllowance.current?.();
    }
  };

  return (
    <>
      {gateNode}
      <a href="#main" className="skip-link">Skip to content</a>
      <Nav />

      {/* HERO */}
      <main id="main">
      <section
        className="relative overflow-hidden min-h-[100dvh]"
        data-testid="hero-section"
      >
        {/* 3D particle plume — full-bleed background layer */}
        <Suspense fallback={<div aria-hidden="true" className="hero-poster absolute inset-0 z-0 pointer-events-none" />}>
          <ParticlePlumeScene className="absolute inset-0 z-0 pointer-events-none" />
        </Suspense>

        {/* Depth vignette overlay */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-[5] pointer-events-none bg-gradient-to-b from-[#e8e4df]/40 via-transparent to-[#e8e4df]/80"
        />

        {/* Centre scrim, mobile only.
            The vignette above is `via-transparent` through its middle - which is
            exactly the band the headline and body copy occupy. On desktop the
            plume is wide and sparse there so it costs nothing, but on a phone the
            ribbon now runs through the text at its densest and brightest, and
            mid-grey monospace over a mid-tone particle field is the least legible
            pairing on the page. This lifts the background back up under the copy
            and fades out well before the edges, so the plume still reads at full
            strength around it. `md:hidden` - desktop is untouched. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-[6] pointer-events-none md:hidden bg-[radial-gradient(ellipse_78%_42%_at_50%_54%,rgba(232,228,223,0.94)_0%,rgba(232,228,223,0.82)_40%,rgba(232,228,223,0.45)_65%,rgba(232,228,223,0)_82%)]"
        />

        {/* Hero content sits on top */}
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 py-12 max-w-4xl mx-auto min-h-[100dvh]">
          {/* The label used to wrap mid-phrase on a phone, breaking after "LIVE"
              and orphaning "ANALYSIS" on its own line while the badge stretched
              to full width. The break is now deliberate: two stacked lines below
              sm, one line with the separator above it. */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/40 border border-black/10 backdrop-blur-md shadow-sm mb-7 sm:mb-8">
            <span className="text-[#e88a51] text-xs shrink-0">✨</span>
            <span className="text-[10px] sm:text-[11px] font-mono-custom tracking-widest uppercase text-neutral-700 font-medium text-center leading-snug">
              Commercial Intelligence
              <span className="hidden sm:inline"> · </span>
              <br className="sm:hidden" />
              Live Analysis
            </span>
          </div>
          <h1
            data-testid="hero-headline"
            className="text-5xl sm:text-6xl md:text-7xl font-serif-custom tracking-tight text-neutral-900 leading-[1.08] mb-6"
          >
            A dedicated analyst <br />
            <span className="italic font-normal">for every operator's</span> margin.
          </h1>
          {/* Two blocks doing different jobs: the positioning claim, then the
              argument for it.

              The claim is set in the display serif so it reads as a statement
              rather than as body copy, and the couplet is split deliberately -
              "sweep / audit" is a parallel construction, and split, the two
              products line up visually the way they line up commercially.

              It sits BELOW the headline in both size and tone (1.85rem against
              the headline's 3.6rem ceiling, neutral-800 against neutral-900).
              Two serif blocks at the same weight would leave a reader unsure
              which one is the headline - the step down is what prevents that,
              so do not raise either value without checking the other. */}
          <div
            data-testid="hero-subtext"
            className="max-w-3xl mb-10"
          >
            <p className="font-serif-custom text-[1.5rem] sm:text-[1.85rem] text-neutral-800 leading-[1.25] mb-5">
              The sweep reads what a store publishes.<br />
              <span className="italic">The audit reads the business.</span>
            </p>
            {/* This line sits over the densest part of the particle plume, and
                noise intersects the glyphs however heavy the weight is. The fix
                is a halo in the ground colour rather than a panel: it lifts the
                text off the particles without putting a visible box in the
                middle of a clean hero, and it costs nothing on the plain areas
                where the plume is sparse.

                Weight 500 (Plus Jakarta Sans loads 400/500/600) and #404040
                instead of #525252 do the rest. Both are contrast-safe on the
                putty ground - #525252 measures 6.17:1 there, and #404040 is
                darker still, so this improves the measured ratio rather than
                trading it away. The particle scene itself is untouched. */}
            <p
              className="font-sans-alt font-medium text-[15px] sm:text-base text-[#404040] leading-relaxed max-w-2xl mx-auto"
              style={{
                textShadow:
                  "0 0 10px rgba(232,228,223,0.95), 0 0 22px rgba(232,228,223,0.85), 0 1px 2px rgba(232,228,223,0.9)",
              }}
            >
              Pricing tools hand you another dashboard to read — Margin Lens hands you the
              decision: where margin is leaking, what to change, and the measured number behind
              every call.
            </p>
          </div>
          {/* Both buttons were w-full on mobile, so two 343px slabs stacked to
              dominate the fold. They now size to their content and sit on one
              row - still a 44px touch target, which is the floor worth keeping. */}
          <div className="flex flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href="#intake"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 sm:gap-2.5 bg-black hover:bg-neutral-800 text-white font-mono-custom text-[13px] sm:text-sm px-5 sm:px-7 py-3 sm:py-3.5 rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 transform hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-current shrink-0" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
              <span>Run a free sweep</span>
            </a>
            <a
              href="#pricing"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-1.5 sm:gap-2 border border-neutral-400 hover:border-black text-neutral-800 font-mono-custom text-[13px] sm:text-sm px-5 sm:px-7 py-3 sm:py-3.5 rounded-full bg-white/30 backdrop-blur-sm transition-all duration-200 hover:bg-white/60 shadow-sm cursor-pointer whitespace-nowrap"
            >
              <span>See pricing</span>
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* METHOD - honest, verifiable claims only */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-20">
        <div className="tick mb-8 pb-4 border-b border-[#ddd8ce]">The method, stated plainly</div>
        <div className="grid md:grid-cols-3 gap-px bg-[#c9c3b8] border border-[#c9c3b8] rounded-[8px] overflow-hidden shadow-2">
          <div data-testid="stat-1" className="relative bg-[#dcd7ce] p-8 md:p-10 flex flex-col items-center text-center overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#e88a51]" />
            <div className="w-11 h-11 rounded-full bg-[#171717] flex items-center justify-center mb-5 shrink-0">
              <Radio size={18} className="text-[#e88a51]" />
            </div>
            <div className="font-serif-display text-6xl text-[#171717] leading-none">Two</div>
            <div className="tick mt-2 text-[#9a3412]">Live feeds read</div>
            <StatGraphic kind="feeds" />
            <p className="mt-3 font-mono-tech text-[12px] text-[#404040] max-w-[220px]">Your public product feed and your competitor's, read side by side and compared.</p>
          </div>
          <div data-testid="stat-2" className="relative bg-[#dcd7ce] p-8 md:p-10 flex flex-col items-center text-center overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#e88a51]" />
            <div className="w-11 h-11 rounded-full bg-[#171717] flex items-center justify-center mb-5 shrink-0">
              <Layers size={18} className="text-[#e88a51]" />
            </div>
            <div className="font-serif-display text-6xl text-[#171717] leading-none">Four</div>
            <div className="tick mt-2 text-[#9a3412]">Modules unlocked free</div>
            <StatGraphic kind="modules" />
            <p className="mt-3 font-mono-tech text-[12px] text-[#404040] max-w-[220px]">Margin risk signals, catalog density, price positioning, unit &amp; pack economics. Four deeper audits need your P&amp;L.</p>
          </div>
          <div data-testid="stat-3" className="relative bg-[#dcd7ce] p-8 md:p-10 flex flex-col items-center text-center overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#e88a51]" />
            <div className="w-11 h-11 rounded-full bg-[#171717] flex items-center justify-center mb-5 shrink-0">
              <Ban size={18} className="text-[#e88a51]" />
            </div>
            <div className="font-serif-display text-6xl text-[#171717] leading-none">Zero</div>
            <div className="tick mt-2 text-[#9a3412]">Invented numbers</div>
            <StatGraphic kind="gaps" />
            <p className="mt-3 font-mono-tech text-[12px] text-[#404040] max-w-[220px]">Where public data can't answer, the cell is marked unavailable — never estimated.</p>
          </div>
        </div>
      </section>

      {/* PRODUCT — SEE IT IN ACTION (intake) */}
      <section id="product" className="border-t border-[#c9c3b8] bg-[#dcd7ce]/60">
        <div className="max-w-[1120px] mx-auto px-6 md:px-10 py-12 md:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="tick mb-4">Live briefing</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.02] text-[#171717]" data-testid="product-heading">
              See Margin Lens in action.
            </h2>
            <p className="mt-5 font-mono-tech text-[13px] text-[#404040]">
              Enter your storefront and a competitor's. You'll get an editorial briefing with
              three fully-unlocked modules, every figure measured from their live public product feeds.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <IntakeForm
              onSubmit={handleSubmit}
              loading={loading}
              sweepsRemaining={sweepsRemaining}
            />
          </div>
        </div>
      </section>

      {/* SIMULATION + REPORT (dynamic)
          Every child here is conditional on `phase`, so in the idle state this
          rendered an empty <section> that still carried py-16 - 128px of dead
          space between Product and Briefing on every first visit. The wrapper is
          now mounted only when it has something to hold. The ref must survive
          for scrollIntoView, so the element still exists in every phase that
          actually scrolls to it. */}
      {phase !== "idle" && (
      <section ref={reportSectionRef} className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16 space-y-10">
        {phase === "simulating" && (
          <Suspense fallback={null}>
            <Simulation
              visible
              stage={progress.stage}
              index={progress.index}
              count={progress.count}
              note={progress.note}
            />
          </Suspense>
        )}
        {phase === "no_feed" && (
          <div className="card-cream p-8 md:p-10" data-testid="sweep-no-feed">
            <div className="tick mb-3 text-[#9a3412]">Nothing measurable</div>
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#171717] leading-tight mb-4">
              {noFeedHeading(noFeedDetail)}
            </h3>

            {/* One line per store, naming what actually happened to it. A typo
                and a firewall are different problems with different fixes, and
                "no product feed" is untrue for both. */}
            {noFeedDetail && (
              <ul className="space-y-3 mb-5 max-w-2xl" data-testid="no-feed-reasons">
                {["your", "competitor"].map((side) => {
                  const d = noFeedDetail[side];
                  if (!d) return null;
                  return (
                    <li key={side} className="font-mono-tech text-[13px] leading-relaxed">
                      <span className="text-[#171717] font-medium">{d.domain}</span>
                      <span className="text-[#525252]"> — {NO_FEED_REASON[d.reason] || NO_FEED_REASON.no_catalog}</span>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* One store readable is not half a sweep - every comparative line
                in the briefing would be blank, and the reader could not tell
                that was the competitor's doing rather than a finding. */}
            {failedSides(noFeedDetail).length === 1 && (
              <p className="font-mono-tech text-[13px] text-[#171717] leading-relaxed max-w-2xl mb-4">
                {failedSides(noFeedDetail)[0] === "competitor" ? (
                  <>
                    The other store read fine. A sweep compares two storefronts,
                    so with one of them unreadable every comparison in the
                    briefing would be blank —{" "}
                    <button
                      type="button"
                      onClick={() => { setNoFeedDetail(null); setPhase("idle"); }}
                      className="underline text-[#9a3412]"
                      data-testid="try-another-competitor"
                    >
                      try a different competitor
                    </button>
                    . This did not use one of your free sweeps.
                  </>
                ) : (
                  <>
                    The competitor read fine; it is your own storefront we could
                    not reach. Check the domain first — if it is right, the
                    paid audit works directly from your figures and does not
                    depend on a readable feed. This did not use one of your free
                    sweeps.
                  </>
                )}
              </p>
            )}

            <p className="font-mono-tech text-[13px] text-[#525252] leading-relaxed max-w-2xl">
              The sweep reads whatever product feed a store publishes openly —
              Shopify, WooCommerce, or a sampled feed from the sitemap. With
              nothing readable there is nothing to measure, and rather than
              render a briefing full of blanks, we stopped.
              {noFeedDetail && anyBlocked(noFeedDetail) && (
                <>
                  {" "}We identify ourselves on every request and honour
                  robots.txt, which is set out in our{" "}
                  <Link to="/legal/methodology" className="underline text-[#9a3412]">
                    data methodology
                  </Link>
                  . A store that turns identified crawlers away stays turned
                  away — we do not disguise the request to get around it.
                </>
              )}
              {" "}The paid audit can still work directly from your own figures;
              use the contact form and say so.
            </p>
          </div>
        )}
      </section>
      )}

      {/* FEATURE STACK */}
      <section id="briefing" className="border-t border-[#c9c3b8] bg-[#e8e4df]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-20">
          <div className="max-w-3xl mb-12">
            <div className="tick mb-4">Included in every Margin Risk Sweep</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.02] text-[#171717]" data-testid="briefing-heading">
              Four measured modules, in every free sweep.
            </h2>
            <p className="mt-5 font-mono-tech text-[13px] text-[#404040] max-w-xl">
              Most operator dashboards are full of pretty green tiles that hide operational cash bleeding. Every free sweep measures four of the places it actually hides.
            </p>
          </div>

          <div className="space-y-6">
            {FEATURES.map((f, i) => (
              <article
                key={f.tag}
                data-testid={`feature-${i}`}
                className="card-cream card-lift grid md:grid-cols-[240px_1fr_1fr] gap-8 p-8 md:p-10"
              >
                <div>
                  <div className="tick mb-3">{f.tag}</div>
                  <div className="w-10 h-10 border border-[#c9c3b8] flex items-center justify-center mb-4">
                    <f.icon size={16} className="text-[#171717]" />
                  </div>
                </div>
                <div>
                  <h3 className="font-serif-display text-[28px] md:text-[32px] text-[#171717] leading-[1.1] mb-4">
                    {f.title}
                  </h3>
                  <p className="font-mono-tech text-[13px] text-[#404040] leading-relaxed">
                    {f.lede}
                  </p>
                </div>
                <ul className="space-y-3">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 font-mono-tech text-[12px] text-[#171717] leading-relaxed">
                      <span className="mt-[7px] w-1.5 h-1.5 bg-[#e88a51] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      </main>

      <LockedAudits onUnlock={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} />
      {/* Attribution only: which free sweep preceded a paid audit. The audit
          re-reads both catalogues live at generation time (server.py
          _run_audit), so nothing analytical depends on this id being fresh. */}
      <PricingMatrix reportId={lastReportId} />
      <Footer />
    </>
  );
}
